import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import axios from 'axios';
import { saveAs } from 'file-saver';

const HISTORY_URL = 'https://serveravotasautofleet.ccsoftwares.com/stock-redemptions/history';

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDates = () => {
  const today = new Date();
  return {
    fromDate: formatInputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    toDate: formatInputDate(today),
  };
};

const defaultDates = getDefaultDates();

const initialFilters = {
  fromDate: defaultDates.fromDate,
  toDate: defaultDates.toDate,
  redemptionNo: '',
  itemName: '',
  createdBy: '',
  referenceNote: '',
};

const formatQuantity = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatCreatedAt = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const escapeCsvValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const toCsv = (rows) => {
  const headers = [
    'Redemption Date',
    'Redemption No.',
    'Item Name',
    'Quantity Redeemed',
    'Redeemed By',
    'Reference / Note',
    'Created At',
  ];
  const lines = rows.map((row) => [
    row.redemption_date,
    row.redemption_no,
    row.item_name,
    row.quantity,
    row.created_by,
    row.reference_note || '',
    formatCreatedAt(row.created_at),
  ].map(escapeCsvValue).join(','));

  return `\uFEFF${headers.map(escapeCsvValue).join(',')}\n${lines.join('\n')}`;
};

function StockRedemptionHistoryReport() {
  const [filters, setFilters] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ redemptionCount: 0, totalQuantity: 0, uniqueItems: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const invalidDateRange = Boolean(filters.fromDate && filters.toDate && filters.fromDate > filters.toDate);

  const requestFilters = useMemo(() => Object.fromEntries(
    Object.entries(filters).filter(([, value]) => String(value).trim() !== '')
  ), [filters]);

  const loadHistory = useCallback(async () => {
    if (invalidDateRange) {
      setRows([]);
      setTotal(0);
      setSummary({ redemptionCount: 0, totalQuantity: 0, uniqueItems: 0 });
      setError('The from date cannot be after the to date.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.get(HISTORY_URL, {
        params: { ...requestFilters, page: page + 1, pageSize: rowsPerPage },
      });
      setRows(response.data?.rows || []);
      setTotal(Number(response.data?.total || 0));
      setSummary(response.data?.summary || { redemptionCount: 0, totalQuantity: 0, uniqueItems: 0 });
    } catch (requestError) {
      setRows([]);
      setTotal(0);
      setSummary({ redemptionCount: 0, totalQuantity: 0, uniqueItems: 0 });
      setError(requestError.response?.data?.error || 'Unable to load stock redemption history.');
    } finally {
      setLoading(false);
    }
  }, [invalidDateRange, page, requestFilters, rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(loadHistory, 300);
    return () => clearTimeout(timer);
  }, [loadHistory]);

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setPage(0);
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setPage(0);
  };

  const downloadReport = async () => {
    if (!total || invalidDateRange) return;
    setDownloading(true);
    setError('');
    try {
      const exportRows = [];
      const exportPageSize = 100;
      const totalPages = Math.ceil(total / exportPageSize);

      for (let exportPage = 1; exportPage <= totalPages; exportPage += 1) {
        const response = await axios.get(HISTORY_URL, {
          params: { ...requestFilters, page: exportPage, pageSize: exportPageSize },
        });
        exportRows.push(...(response.data?.rows || []));
      }

      const dateLabel = filters.fromDate || filters.toDate
        ? `${filters.fromDate || 'start'}-to-${filters.toDate || 'today'}`
        : 'all-dates';
      saveAs(
        new Blob([toCsv(exportRows)], { type: 'text/csv;charset=utf-8' }),
        `named-stock-redemption-history-${dateLabel}.csv`
      );
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to download the report.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Stock Redemption History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Analyse redeemed stock by date, item, redemption reference, user, and note.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={downloadReport}
          disabled={!total || loading || downloading || invalidDateRange}
          sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
        >
          {downloading ? 'Preparing CSV…' : 'Download CSV'}
        </Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">Redemption Transactions</Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {Number(summary.redemptionCount || 0).toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">Total Quantity Redeemed</Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {formatQuantity(summary.totalQuantity)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F1F5F9', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">Unique Stock Items</Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {Number(summary.uniqueItems || 0).toLocaleString()}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={filters.fromDate}
              onChange={(event) => updateFilter('fromDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={filters.toDate}
              onChange={(event) => updateFilter('toDate', event.target.value)}
              InputLabelProps={{ shrink: true }}
              error={invalidDateRange}
              helperText={invalidDateRange ? 'Must be on or after the from date' : ' '}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <TextField
              fullWidth
              label="Redemption No."
              placeholder="e.g. SR-20260813"
              value={filters.redemptionNo}
              onChange={(event) => updateFilter('redemptionNo', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <TextField
              fullWidth
              label="Stock Item Name"
              placeholder="Search item name"
              value={filters.itemName}
              onChange={(event) => updateFilter('itemName', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <TextField
              fullWidth
              label="Redeemed By"
              placeholder="Search user name"
              value={filters.createdBy}
              onChange={(event) => updateFilter('createdBy', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={5}>
            <TextField
              fullWidth
              label="Reference / Note"
              placeholder="Search reference details or notes"
              value={filters.referenceNote}
              onChange={(event) => updateFilter('referenceNote', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} lg={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterAltOffIcon />}
              onClick={resetFilters}
              sx={{ textTransform: 'none', minHeight: 56 }}
            >
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small" aria-label="Stock redemption history">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 700 }}>Redemption Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Redemption No.</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Stock Item</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Quantity</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Redeemed By</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Reference / Note</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length ? rows.map((row) => (
                <TableRow key={row.redemption_detail_id} hover>
                  <TableCell>{row.redemption_date}</TableCell>
                  <TableCell>{row.redemption_no}</TableCell>
                  <TableCell>{row.item_name}</TableCell>
                  <TableCell align="right">{formatQuantity(row.quantity)}</TableCell>
                  <TableCell>{row.created_by || '-'}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>{row.reference_note || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCreatedAt(row.created_at)}</TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No stock redemption records match the selected filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
}

export default StockRedemptionHistoryReport;
